import React, { useContext, useEffect } from 'react';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import { DeclaredComponent } from '../DynamicComponent';
import { GlobalStateContext } from '../../context/GlobalStateProvider';
import { Divider } from 'antd';
import { Expression } from '../metadata/Expression';
import { ModelContext } from '../metadata/ModelSource';
import {FormContext, MDForm} from '../metadata/MDForm';
import { ProductInfoContext } from '../data/ApplicationDataSource';
import { formatMoneyAmountNoDecimal } from '../../utils/formatUtils';
import { coveragesToSkip } from '../forms/coverages/CoverageTable'
import { isApplicable } from "../metadata/Field";

const isOdd = (number) => {
  const value = number % 2;
  return (value === 1);
};

const determineJustifyContent = (index) => {
  if (index === 1) {
    return 'flex-end';
  } else if (index === 2) {
    return 'center';
  } else {
    return 'flex-start';
  }
};

const isRowApplicable = (tableData, pathPrefix=undefined) => {
  let useRow = true;
  tableData.map(({ row }, rowIndex) => {
    // Step through the labels, limits, and data fields looking for named attributes
    row.map((data, colIndex) => {
      const columnItemName = Object.keys(data)[0];
      if (columnItemName === 'lineLimit') {
        ;  // implicit true
      } else if (columnItemName === 'Expression') {
        ;  // implicit true
      } else {
        // Typically a Field or PremiumSum object
        const obj = data[columnItemName];
        // Check to see if this data item has an attribute with applicability info
        if (obj.hasOwnProperty('attr')) {
          // This column indicates a metadata attribute name, so run with it
          useRow &= isApplicable(obj.attr, pathPrefix);
        }
      }
    })
  })
  return (useRow);
};

const MobileTable = ({ tableData, currentIndex, labels }) => {
  const styles = {
    column: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      alignContent: 'center',
      height: '140px',
      padding: '8px 16px',
      backgroundColor: isOdd(currentIndex) ? '#f8f9fa' : '',
    },
  };

  const Label = ({ labels, currentIndex }) => {
    return (
      <Col span={12} style={{ display: 'flex', justifyContent: 'flex-start' }}>
        <DeclaredComponent declaration={labels[currentIndex]} />
      </Col>
    );
  };

  return (
    <div style={styles.column}>
      {tableData.map(({ row }, index) => {
        const currentIndex = index;
        return (
          <Row key={index} style={{ display: 'flex', alignItems: 'center' }}>
            {row.map((data, index) => (
              <>
                <Label labels={labels} currentIndex={currentIndex} />
                <Col
                  span={12}
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                  }}
                  key={index}
                >
                  {Object.keys(data)[0] === 'lineLimit' ? (
                      <LineCoverageLimit lineLimitAttr={data.lineLimit}/>
                  ) : (
                      <DeclaredComponent declaration={data} />
                  )}
                </Col>
              </>
            ))}
          </Row>
        );
      })}
    </div>
  );
};

const LineCoverageLimit = ({lineLimitAttr}) => {
  const model = useContext(ModelContext);
  const form = useContext(FormContext);
  const isActive =  model.getValue(`${form.prefix}coverages{coverageCd:${lineLimitAttr}}.status`) === 'Active';
  /*WaiverOfCollision treated differently
    waiver of collision deductible => yes => active => return yes
    waiver of collision deductible => no => deleted => return no
    all other line coverages fall into else
  */
  if(lineLimitAttr === 'WaiverOfCollision'){
    return isActive ? <div>Yes</div> : <div>No</div>
  }
   else{
    if(!isActive){
      return <div>No Coverage</div>
    }

    const limits=  model.getValue(`${form.prefix}coverages{coverageCd:${lineLimitAttr}}.limits`);
    const limitArray = limits && limits.map((limit) => '$' +formatMoneyAmountNoDecimal(limit.value));
    const limitConcat = limitArray && limitArray.join(' / ');
    return <div>{limitConcat}</div>
  }
}

const RowData = ({tableData}) => {
  return tableData.map(({ row }, index) => {
    const currentIndex = index;
    return row.map((item, index) => (
      <Col
        key={index}
        span={currentIndex === 2 ? 6 : 9}
        style={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: determineJustifyContent(currentIndex),
        }}
      >
        {Object.keys(item)[0] === 'lineLimit' ? (
           <LineCoverageLimit lineLimitAttr={item.lineLimit}/>
        ) : (
            <DeclaredComponent declaration={item} />
        )}
      </Col>
    ));
  });
};

const DesktopTable = ({ tableData }) => {

  return (
    <Row style={{ margin: '16px 0px' }}>
      <RowData tableData={tableData}/>
    </Row>
  );
};

const RiskHeader = ({  isDesktop }) => {

  return (
    <Row style={{ marginTop: isDesktop ? '36px' : '' }}>
      <Col span={24}>
        <div className="form-title form-title-margin">
          <Expression
            value={
              '#{Vehicle.ModelYr} #{Vehicle.Manufacturer} #{Vehicle.Model}'
            }
          />
        </div>
      </Col>
    </Row>
  );
};

const ColumnHeaders = ({ isDesktop, labels }) => {
  if (!isDesktop) return null;

  const Labels = () => {
    return labels.map((label, index) => (
      <Col
        span={index === 2 ? 6 : 9}
        key={index}
        style={{
          display: 'flex',
          justifyContent: determineJustifyContent(index),
        }}
      >
        <DeclaredComponent declaration={label} style={{ fontWeight: 'bold' }} />
      </Col>
    ));
  };

  return (
    <Row>
      <div style={{ paddingBottom: '8px', width:'100%', display:'flex' }}>
        <Labels />
      </div>
      <Divider />
    </Row>
  );
};

export const InfoTable = ({ content, labels, isRisk, lineCd, ...props }) => {
  // Note: lineCd parameter is required only when using a lineCoverage block inside InfoTable
  const {prefix} = useContext(FormContext);
  if (!content) return null;
  const { isDesktop } = useContext(GlobalStateContext);
  const { productSetup } = useContext(ProductInfoContext);
  const model = useContext(ModelContext);
  const applicationModel = useContext(ModelContext);

  const lineCoverage = (lineCoverages) =>{
    return(
            lineCoverages.map((lineCov,index) => {
              // Line coverages use values from the "line" data group, rather than the container's data group
              const pathPrefix = 'lines{lineCd:' + lineCd + '}.';
              const {tableData} = lineCov
              return table(tableData, index, pathPrefix)
            })
    )
  }

  const table = ( tableData, index, pathPrefix=undefined ) => {
    if ( ! isRowApplicable(tableData, pathPrefix)) {
      return (null);
    }
    if (isDesktop) {
      return (
        <div>
          <DesktopTable
            tableData={tableData}
          />
        </div>
      );
    }
    return (
      <div>
        <MobileTable
          labels={labels}
          tableData={tableData}
          currentIndex={index}
        />
      </div>
    );
  };

  return (
    <div>
      {isRisk && <RiskHeader isDesktop={isDesktop} />}
      <ColumnHeaders isDesktop={isDesktop} labels={labels} isRisk={isRisk} />
      {content &&
        content.map((data, index) => {
          const { tableData, additionalCoverages, lineCoverages } = data;
          if(lineCoverages !== undefined){
              return(lineCoverage(lineCoverages))
          }

          // Return table with coverages that are changeable
          if (tableData !== undefined && additionalCoverages === undefined) {
            return table(tableData, index);
          }

          // Return table with additional coverages that cannot be changed by user
          const allAvailableCoverages = model.getValue(prefix + 'coverages');

          const coveragesToMap = allAvailableCoverages ? allAvailableCoverages.filter(
            (currentCoverage) => {
              if (coveragesToSkip.includes(currentCoverage.coverageCd)) {
                return;
              } else {
                return currentCoverage;
              }
            }
          ) : null;

          if (!coveragesToMap) return null;

          return (
            <div style={{ marginTop: '-8px' }}>
              {coveragesToMap.map((data, index) => {
                const tableData = [
                  {
                    row: [
                      {
                        Expression: {
                          style: {
                            fontWeight: 'bold',
                            paddingTop: 4,
                          },
                          value: data.description,
                        },
                      },
                    ],
                  },
                  {
                    row: [
                      {
                        Expression: {
                          value: '',
                        },
                      },
                    ],
                  },
                  {
                    row: [
                      {
                        MoneyAmount: {
                          style: {
                            fontWeight: 'bold',
                          },
                          value: data.writtenPremiumAmt,
                        },
                      },
                    ],
                  },
                ];

                return table(tableData, index);
              })}
            </div>
          );
        })}
    </div>
  );
};
